import { useState, useEffect, useRef } from 'react'
import { UserService } from '../services/User'

const useEventAlert = (
  endTime,
  alertMinutesBefore = 5,
  onAlertTime = () => {},
  useServerTime = true
) => {
  const [shouldAlert, setShouldAlert] = useState(false)
  const timerRef = useRef(null)
  const hasAlertTriggered = useRef(false)
  const [serverTimeOffset, setServerTimeOffset] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [debugInfo, setDebugInfo] = useState({})
  const [hasEndTimeUpdated, setHasEndTimeUpdated] = useState(false)

  // 🔧 SOLUCIÓN: Resetear el flag cuando endTime cambia
  useEffect(() => {
    hasAlertTriggered.current = false
    setShouldAlert(false)
  }, [endTime])

  // Función para obtener el tiempo actual (con offset del servidor si useServerTime=true)
  const getCurrentTime = () => {
    if (useServerTime) {
      // Aplicar el offset calculado al tiempo actual del cliente
      return new Date(Date.now() + serverTimeOffset)
    } else {
      return new Date()
    }
  }

  // Función para parsear la fecha de finalización correctamente
  const parseEndTime = (timeStr) => {
    if (!timeStr) return null

    // Si es un objeto Date, simplemente lo devolvemos
    if (timeStr instanceof Date) return timeStr

    try {
      // Intentamos crear un Date a partir del string
      const parsedDate = new Date(timeStr)

      // Verificar si la fecha es válida
      if (isNaN(parsedDate.getTime())) {
        console.log('Info: La fecha no está disponible o no es válida:', timeStr)
        return null
      }

      return parsedDate
    } catch (err) {
      console.log('Info: Error al parsear la fecha:', err)
      return null
    }
  }

  // Efecto para detectar cuando endTime cambia a un valor no nulo
  useEffect(() => {
    if (endTime && !hasEndTimeUpdated) {
      setHasEndTimeUpdated(true)
    }
  }, [endTime, hasEndTimeUpdated])

  // Efecto para sincronizar con el tiempo del servidor
  useEffect(() => {
    let isMounted = true

    if (useServerTime) {
      const syncServerTime = async () => {
        try {
          // Registramos el tiempo de envío de la solicitud
          const clientTimeBeforeRequest = Date.now()

          // Obtenemos el tiempo del servidor
          const response = await UserService.getHour()

          if (!isMounted) return

          if (!response || !response.hour) {
            console.log('Info: La respuesta del servidor no contiene el campo "hour"', response)
            setIsLoading(false)
            return
          }

          // Registramos el tiempo de recepción de la respuesta
          const clientTimeAfterRequest = Date.now()

          // Calculamos la latencia aproximada (ida y vuelta) y su mitad (solo ida)
          const roundTripLatency = clientTimeAfterRequest - clientTimeBeforeRequest
          const oneWayLatency = Math.floor(roundTripLatency / 2)

          // Obtenemos la hora del servidor - directamente como Date
          const serverTime = new Date(response.hour)

          if (isNaN(serverTime.getTime())) {
            console.log('Info: La fecha del servidor no es válida:', response.hour)
            setIsLoading(false)
            return
          }

          // Ajustamos por la latencia (sumamos la latencia de ida para compensar el retraso)
          const adjustedServerTime = serverTime.getTime() + oneWayLatency

          // Calculamos la diferencia entre el tiempo del servidor y el tiempo del cliente
          const offset = adjustedServerTime - Date.now()
          setServerTimeOffset(offset)

          // Timezone del cliente
          const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

          // Guardar información de depuración
          setDebugInfo({
            serverTimeRaw: response.hour,
            serverTime: serverTime.toISOString(),
            clientTime: new Date().toISOString(),
            offset: offset,
            latency: roundTripLatency,
            clientTimezone: clientTimezone,
          })

          setIsLoading(false)
        } catch (error) {
          console.log('Info: Error al sincronizar con el tiempo del servidor:', error)
          if (isMounted) {
            setIsLoading(false)
          }
        }
      }

      syncServerTime()

      // Sincronizar cada 10 minutos para mantener precisión
      const syncInterval = setInterval(syncServerTime, 10 * 60 * 1000)

      return () => {
        isMounted = false
        clearInterval(syncInterval)
      }
    } else {
      setIsLoading(false)
    }
  }, [useServerTime])

  // Efecto principal para configurar la alerta
  useEffect(() => {
    // No continuar si aún estamos cargando la sincronización del tiempo
    if (isLoading) return

    // Limpiar cualquier temporizador existente
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    // Si no hay endTime o aún no se ha actualizado, simplemente no hacemos nada
    // pero no mostramos error para permitir que la aplicación cargue
    if (!endTime) {
      return
    }

    // Parsear la fecha de finalización
    const endTimeDate = parseEndTime(endTime)

    // Si no se pudo parsear la fecha pero endTime existe, simplemente retornamos sin mostrar error
    if (!endTimeDate) {
      return
    }

    const currentTime = getCurrentTime()

    // Obtenemos fechas en formato local para mejor comprensión en logs
    // const endTimeLocal = formatDateForDebug(endTimeDate)
    // const currentTimeLocal = formatDateForDebug(currentTime)

    // Calcular cuando debe mostrarse la alerta
    const alertTime = new Date(endTimeDate.getTime() - alertMinutesBefore * 60 * 1000)
    // const alertTimeLocal = formatDateForDebug(alertTime)
    // Para alertas de finalización (0 minutos antes), verificamos si realmente finalizó
    if (alertMinutesBefore === 0) {
      // Si el tiempo actual es posterior al tiempo de finalización, se disparará la alerta
      if (currentTime >= endTimeDate) {
        if (!hasAlertTriggered.current) {
          setShouldAlert(true)
          onAlertTime()
          hasAlertTriggered.current = true
        }
        return
      }
    }
    // Para alertas previas a la finalización
    else {
      // Si ya es tiempo de mostrar la alerta
      if (currentTime >= alertTime) {
        if (!hasAlertTriggered.current) {
          setShouldAlert(true)
          onAlertTime()
          hasAlertTriggered.current = true
        }
        return
      }
    }

    // Calcular el tiempo restante hasta la alerta (en ms)
    const timeUntilAlert =
      alertMinutesBefore === 0
        ? endTimeDate.getTime() - currentTime.getTime()
        : alertTime.getTime() - currentTime.getTime()

    // Asegurarse de que el tiempo sea positivo
    if (timeUntilAlert <= 0) {
      console.log('⚠️ Tiempo hasta la alerta es negativo:', timeUntilAlert)
      return
    }

    // Configurar el temporizador para mostrar la alerta
    timerRef.current = setTimeout(() => {
      setShouldAlert(true)
      onAlertTime()
      hasAlertTriggered.current = true
    }, timeUntilAlert)

    // Función de limpieza que se ejecuta al desmontar el componente
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [endTime, alertMinutesBefore, onAlertTime, isLoading, serverTimeOffset, hasEndTimeUpdated])

  // Función para resetear el estado de la alerta
  const resetAlert = () => {
    setShouldAlert(false)
    hasAlertTriggered.current = false
  }

  return { shouldAlert, resetAlert, isLoading, debugInfo }
}

export default useEventAlert
